import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Envelope,
  User,
  Buildings,
  MapPin,
  Phone,
  LinkedinLogo,
  TwitterLogo,
  Briefcase,
  CaretDown,
  Tag,
} from '@phosphor-icons/react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import TagAutocomplete from '../../../components/ui/TagAutocomplete';
import { Contact, useContactStore } from '../../../stores/contactStore';
import { usePermission } from '../../../hooks/usePermission';
import { useTrialStatus } from '../../../hooks/useTrialStatus';
import { useScrollLock } from '../../../lib/useScrollLock';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { planLimitToastMessage } from '../../../lib/planLimitMessages';
import { SENIORITY_OPTIONS as CANONICAL_SENIORITY_OPTIONS } from '../../../lib/seniority';
import { isContactFormDirty, type ContactFormState } from './contactFormDirty';

interface AddContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  editContact?: Contact | null;
}

const SENIORITY_OPTIONS = [
  { value: '', label: 'Select level' },
  ...CANONICAL_SENIORITY_OPTIONS,
];

// Values are the same five canonical buckets used by the Trigger/Condition
// node filters (frontend/src/pages/workflows/components/config/
// {TriggerConfig,ConditionConfig}.tsx) and backend/app/core/company_size.py,
// so a manually entered size always matches the way workflows and conditions
// filter on it. Labels show the employee-count range each bucket covers.
const COMPANY_SIZE_OPTIONS = [
  { value: '', label: 'Select size' },
  { value: 'startup', label: '1-9' },
  { value: 'small', label: '10-50' },
  { value: 'medium', label: '51-200' },
  { value: 'large', label: '201-1000' },
  { value: 'enterprise', label: '1000+' },
];

// A blank form, shared by the initial state and the reset-on-open effect so the
// 16 field names are declared once. Typed against ContactFormState so a field
// added here without being added to CONTACT_FORM_FIELDS (and therefore skipped
// by the unsaved-changes check) fails to compile.
const EMPTY_FORM: ContactFormState = {
  email: '',
  first_name: '',
  last_name: '',
  job_title: '',
  department: '',
  seniority_level: '',
  company_name: '',
  company_website: '',
  company_industry: '',
  company_size: '',
  phone: '',
  linkedin_url: '',
  twitter_url: '',
  city: '',
  state: '',
  country: '',
};

const FIELD_LABEL =
  'block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]';

// --- Field validation (mirrors the backend validators in
// backend/app/routes/contacts.py so the UI and API reject the same values) ---

// Any Unicode letter. Text fields must have at least one letter, so a purely
// numeric value like "123" is rejected while "O'Brien" / "Jean-Luc" / "John3"
// are accepted.
const LETTER_RE = /\p{L}/u;
// Phone: only digits and common phone punctuation allowed as characters.
const PHONE_CHARS_RE = /^[+\d\s\-()]+$/;

const TEXT_MAX: Record<string, number> = {
  first_name: 100,
  last_name: 100,
  city: 100,
  state: 100,
  country: 100,
  department: 100,
  company_industry: 100,
  job_title: 150,
  company_name: 150,
};

// Fields that live inside the collapsed "Additional details" section. If a
// validation error lands on one of these, we expand the section on submit so
// the user can see it.
const ADDITIONAL_FIELDS = ['department', 'city', 'state', 'country', 'phone', 'linkedin_url', 'twitter_url'];

function textError(label: string, value: string, maxLen: number): string | null {
  const s = value.trim();
  if (!s) return null;
  if (!LETTER_RE.test(s)) return `${label} must contain letters, not just numbers`;
  if (s.length > maxLen) return `${label} must be at most ${maxLen} characters`;
  return null;
}

function phoneError(value: string): string | null {
  const s = value.trim();
  if (!s) return null;
  if (!PHONE_CHARS_RE.test(s)) return 'Phone may contain only digits, spaces, and + - ( )';
  const digits = (s.match(/\d/g) || []).length;
  if (digits < 7 || digits > 15) return 'Phone must contain 7 to 15 digits';
  return null;
}

function urlError(label: string, value: string, domains?: string[]): string | null {
  const s = value.trim();
  if (!s) return null;
  let parsed: URL;
  try {
    parsed = new URL(s);
  } catch {
    return `${label} must be a valid http(s) URL (including https://)`;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return `${label} must be a valid http(s) URL (including https://)`;
  }
  if (domains && domains.length) {
    let host = parsed.hostname.toLowerCase();
    if (host.startsWith('www.')) host = host.slice(4);
    const ok = domains.some((d) => host === d || host.endsWith(`.${d}`));
    if (!ok) return `${label} must be a URL on ${domains.join(' or ')}`;
  }
  return null;
}

export function AddContactModal({ isOpen, onClose, editContact }: AddContactModalProps) {
  useScrollLock(isOpen);
  const { createContact, updateContact, fetchTags, loading } = useContactStore();
  // Analyst holds no contacts.create_edit cell at all (spec 4-E), so both
  // POST and PUT 403 for them. Disable the submit rather than letting the
  // click fail after the form has been filled in (AB#22336). The Edit entry
  // point is hidden too -- this covers any other way in.
  const { canWrite: canEditContacts } = usePermission('contacts', 'create_edit');
  // The Upgrade action only appears for whoever can actually act on it:
  // billing.manage_subscription is Owner/Admin, and Billing is hidden in
  // the nav for everyone else, so the button led to a page that exists
  // only to say billing is not theirs. The message itself still explains
  // the cap, which is the part every role needs.
  const { canWrite: canUpgradePlan } = usePermission('billing', 'manage_subscription')
  const { isTrial } = useTrialStatus();
  const navigate = useNavigate();
  const isEditing = Boolean(editContact);

  const [showAdditional, setShowAdditional] = useState(false);
  const [formData, setFormData] = useState<ContactFormState>({ ...EMPTY_FORM });

  const [tags, setTags] = useState<string[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const titleId = useId();

  // What the form held the moment it opened — an existing contact's values, or
  // a blank form. The unsaved-changes guard compares against this rather than
  // against EMPTY_FORM, otherwise every edit-mode open would read as dirty.
  const baselineRef = useRef<{ values: ContactFormState; tags: string[] }>({
    values: { ...EMPTY_FORM },
    tags: [],
  });

  useEffect(() => {
    const seededValues: ContactFormState = editContact
      ? {
          email: editContact.email || '',
          first_name: editContact.first_name || '',
          last_name: editContact.last_name || '',
          job_title: editContact.job_title || '',
          department: editContact.department || '',
          seniority_level: editContact.seniority_level || '',
          company_name: editContact.company_name || '',
          company_website: editContact.company_website || '',
          company_industry: editContact.company_industry || '',
          company_size: editContact.company_size || '',
          phone: editContact.phone || '',
          linkedin_url: editContact.linkedin_url || '',
          twitter_url: editContact.twitter_url || '',
          city: editContact.city || '',
          state: editContact.state || '',
          country: editContact.country || '',
        }
      : { ...EMPTY_FORM };
    const seededTags = editContact && Array.isArray(editContact.tags) ? [...editContact.tags] : [];

    setFormData(seededValues);
    setTags(seededTags);
    // Snapshot the exact values that were seeded, in the same pass that seeds
    // them, so the two can never drift apart.
    baselineRef.current = { values: seededValues, tags: seededTags };
    setShowAdditional(false);
    setErrors({});
    setShowDiscardConfirm(false);
  }, [editContact, isOpen]);

  const isDirty = isContactFormDirty(
    formData,
    baselineRef.current.values,
    tags,
    baselineRef.current.tags
  );

  // Single funnel for every close path (X, backdrop, Cancel, Escape): with 20
  // fields in play, silently dropping typed data on a stray backdrop click is
  // the expensive failure. Successful submit deliberately bypasses this — see
  // handleSubmit, where the save has already persisted the values.
  const requestClose = useCallback(() => {
    // A save in flight closes the modal itself when it resolves; prompting
    // "discard?" over a pending write would only offer a choice that no longer
    // exists. Cancel is already disabled while loading for the same reason.
    if (loading) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  }, [loading, isDirty, onClose]);

  // Escape was missing entirely — the modal could only be dismissed by mouse.
  // Skipped while the discard prompt is up: that dialog owns Escape (it
  // dismisses itself), so one keypress must not answer both layers.
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || showDiscardConfirm) return;
      // A <Select> menu listens for Escape on `window`, which the event reaches
      // only *after* `document` — so without this check, dismissing the
      // Seniority/Company-size dropdown would also request a modal close (and
      // prompt to discard). An open menu is the only portalled role="listbox"
      // in the tree, so its presence means Escape is already spoken for.
      if (document.querySelector('[role="listbox"]')) return;
      requestClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, showDiscardConfirm, requestClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const setField = (name: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Phone must not accept textual values. Keep only digits and common phone
  // punctuation (+, spaces, hyphens, parentheses) and drop everything else, so
  // letters can't be typed OR pasted into the field (onChange fires on paste).
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = e.target.value.replace(/[^\d+\-()\s]/g, '');
    setFormData((prev) => ({ ...prev, phone: sanitized }));
    if (errors.phone) setErrors((prev) => ({ ...prev, phone: '' }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Required text fields: non-empty + not purely numeric + max length.
    const requiredText: Array<[keyof typeof formData, string]> = [
      ['first_name', 'First name'],
      ['last_name', 'Last name'],
      ['job_title', 'Job title'],
      ['company_name', 'Company name'],
    ];
    requiredText.forEach(([name, label]) => {
      if (!formData[name].trim()) {
        newErrors[name] = `${label} is required`;
      } else {
        const err = textError(label, formData[name], TEXT_MAX[name]);
        if (err) newErrors[name] = err;
      }
    });

    // Optional text fields: validated only when filled.
    const optionalText: Array<[keyof typeof formData, string]> = [
      ['department', 'Department'],
      ['company_industry', 'Industry'],
      ['city', 'City'],
      ['state', 'State'],
      ['country', 'Country'],
    ];
    optionalText.forEach(([name, label]) => {
      const err = textError(label, formData[name], TEXT_MAX[name]);
      if (err) newErrors[name] = err;
    });

    const phoneErr = phoneError(formData.phone);
    if (phoneErr) newErrors.phone = phoneErr;

    const websiteErr = urlError('Company website', formData.company_website);
    if (websiteErr) newErrors.company_website = websiteErr;
    const linkedinErr = urlError('LinkedIn URL', formData.linkedin_url, ['linkedin.com']);
    if (linkedinErr) newErrors.linkedin_url = linkedinErr;
    const twitterErr = urlError('Twitter/X URL', formData.twitter_url, ['twitter.com', 'x.com']);
    if (twitterErr) newErrors.twitter_url = twitterErr;

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      // Reveal the collapsed section if an error is hiding inside it.
      if (ADDITIONAL_FIELDS.some((f) => validationErrors[f])) {
        setShowAdditional(true);
      }
      return;
    }
    const payload = { ...formData, tags };
    try {
      if (isEditing && editContact) {
        await updateContact(editContact.id, payload);
        toast.success('Contact updated successfully');
      } else {
        await createContact(payload);
        toast.success('Contact added successfully');
      }
      // Tags live as a denormalized array on each contact; the sidebar's tag
      // list/counts are derived, so refresh them after an add/remove.
      fetchTags();
      // Straight to onClose, not requestClose: the values are saved, so there
      // is nothing left to discard and no reason to prompt.
      onClose();
    } catch (error: any) {
      const { message, showUpgrade } = planLimitToastMessage(error, {
        isTrial,
        fallback: isEditing ? 'Failed to update contact' : 'Failed to add contact',
      });
      toast.error(message, showUpgrade && canUpgradePlan ? { action: { label: 'Upgrade', onClick: () => navigate('/billing') } } : undefined);
    }
  };

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  return createPortal((
    <>
    <AnimatePresence>
      <div
        className="fixed top-0 left-0 w-screen h-screen z-[100] flex items-center justify-center p-4 backdrop-blur-md"
        onClick={requestClose}
      >
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'w-full max-w-2xl rounded-2xl border bg-card shadow-2xl flex flex-col max-h-[90vh]',
            'border-border/60 dark:bg-[#191a1b] dark:border-white/[0.08]',
            'dark:shadow-[0_24px_64px_rgba(0,0,0,0.65),0_4px_12px_rgba(0,0,0,0.45),inset_0_0_0_1px_rgba(255,255,255,0.02)]'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-border/50 dark:border-white/[0.06] shrink-0">
            <div className="min-w-0">
              <h2
                id={titleId}
                className="text-foreground dark:text-[#f7f8f8] truncate"
                style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px', lineHeight: 1.3 }}
              >
                {isEditing ? 'Edit contact' : 'Add new contact'}
              </h2>
              <p
                className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
                style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
              >
                {isEditing ? 'Update the contact details below.' : 'Fill in the contact details below.'}
              </p>
            </div>
            <button
              onClick={requestClose}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150 flex-shrink-0"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-5 overflow-y-auto">
            <div className="space-y-6">
              {/* Essential */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  {isEditing ? (
                    // Email is the contact's identity — fixed once created. Show
                    // it as static info rather than a field so it never reads as
                    // editable. Create a new contact for a different email.
                    <div>
                      <label className={FIELD_LABEL}>Email</label>
                      <div
                        className={cn(
                          'flex h-10 w-full items-center gap-2 rounded-md px-3 py-2',
                          'border bg-muted/40 border-border/60 text-muted-foreground',
                          'dark:bg-white/[0.03] dark:border-white/[0.08] dark:text-[#8a8f98]'
                        )}
                      >
                        <Envelope className="h-4 w-4 flex-shrink-0" />
                        <span
                          className="truncate"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        >
                          {formData.email}
                        </span>
                      </div>
                      <p
                        className="text-muted-foreground/80 dark:text-[#62666d] mt-1.5"
                        style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                      >
                        Email can't be changed once a contact is created.
                      </p>
                    </div>
                  ) : (
                    <>
                      <Input
                        label="Email *"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={errors.email}
                        icon={<Envelope className="h-4 w-4" />}
                        placeholder="jane.doe@acme.com"
                        required
                      />
                      {!errors.email && (
                        <p
                          className="text-amber-600 dark:text-amber-400/90 mt-1.5"
                          style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                        >
                          Double-check the email — it can't be changed after the contact is created.
                        </p>
                      )}
                    </>
                  )}
                </div>
                <Input
                  label="First name *"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  error={errors.first_name}
                  icon={<User className="h-4 w-4" />}
                  placeholder="Jane"
                  required
                />
                <Input
                  label="Last name *"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  error={errors.last_name}
                  icon={<User className="h-4 w-4" />}
                  placeholder="Doe"
                  required
                />
              </div>

              {/* Professional */}
              <div className="border-t border-border/50 dark:border-white/[0.06] pt-5">
                <h3
                  className="text-foreground dark:text-[#f7f8f8] mb-3 inline-flex items-center gap-2"
                  style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                >
                  <Buildings className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                  Professional details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Job title *"
                    name="job_title"
                    value={formData.job_title}
                    onChange={handleChange}
                    error={errors.job_title}
                    icon={<Briefcase className="h-4 w-4" />}
                    placeholder="Head of Marketing"
                    required
                  />
                  <Input
                    label="Company name *"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    error={errors.company_name}
                    placeholder="Acme Inc."
                    required
                  />
                  <Input
                    label="Industry"
                    name="company_industry"
                    value={formData.company_industry}
                    onChange={handleChange}
                    error={errors.company_industry}
                    placeholder="Software / SaaS"
                  />
                  <Input
                    label="Company website"
                    name="company_website"
                    type="url"
                    value={formData.company_website}
                    onChange={handleChange}
                    error={errors.company_website}
                    placeholder="https://acme.com"
                  />
                </div>
              </div>

              {/* Tags */}
              <div className="border-t border-border/50 dark:border-white/[0.06] pt-5">
                <h3
                  className="text-foreground dark:text-[#f7f8f8] mb-3 inline-flex items-center gap-2"
                  style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                >
                  <Tag className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                  Tags
                </h3>
                <TagAutocomplete selectedTags={tags} onChange={setTags} />
                <p
                  className="text-muted-foreground/80 dark:text-[#62666d] mt-1.5"
                  style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                >
                  Pick an existing tag from the list, or type and press Enter to create a new one. Changes save with the contact.
                </p>
              </div>

              {/* Additional — collapsible dropdown */}
              <div className="border-t border-border/50 dark:border-white/[0.06] pt-5">
                <button
                  type="button"
                  onClick={() => setShowAdditional((v) => !v)}
                  aria-expanded={showAdditional}
                  className={cn(
                    'w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-md border transition-colors duration-150',
                    'bg-muted/30 border-border/50 hover:bg-muted/50 hover:border-border/80',
                    'dark:bg-white/[0.02] dark:border-white/[0.06] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.12]'
                  )}
                >
                  <span
                    className="inline-flex items-center gap-2 text-foreground dark:text-[#f7f8f8]"
                    style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                  >
                    <MapPin className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                    Additional details
                    <span
                      className="text-muted-foreground/80 dark:text-[#62666d]"
                      style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.05px' }}
                    >
                      Optional
                    </span>
                  </span>
                  <CaretDown
                    className={cn(
                      'h-3.5 w-3.5 text-muted-foreground dark:text-[#8a8f98] transition-transform duration-150',
                      showAdditional && 'rotate-180'
                    )}
                    weight="bold"
                  />

                </button>

                <AnimatePresence initial={false}>
                  {showAdditional && (
                    <motion.div
                      key="additional"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-4 pt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <Input
                            label="Department"
                            name="department"
                            value={formData.department}
                            onChange={handleChange}
                            error={errors.department}
                            placeholder="Marketing"
                          />
                          <div>
                            <label className={FIELD_LABEL}>Seniority level</label>
                            <Select
                              value={formData.seniority_level}
                              onChange={(v) => setField('seniority_level', v)}
                              options={SENIORITY_OPTIONS}
                              placeholder="e.g. Director, VP"
                              ariaLabel="Seniority level"
                              className="w-full"
                              buttonClassName="h-10"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <Input
                            label="City"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            error={errors.city}
                            placeholder="San Francisco"
                          />
                          <Input
                            label="State"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            error={errors.state}
                            placeholder="California"
                          />
                          <Input
                            label="Country"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            error={errors.country}
                            placeholder="United States"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <Input
                            label="Phone"
                            name="phone"
                            type="tel"
                            inputMode="tel"
                            value={formData.phone}
                            onChange={handlePhoneChange}
                            error={errors.phone}
                            icon={<Phone className="h-4 w-4" />}
                            placeholder="+1 415 555 0100"
                          />
                          <div>
                            <label className={FIELD_LABEL}>Company size</label>
                            <Select
                              value={formData.company_size}
                              onChange={(v) => setField('company_size', v)}
                              options={COMPANY_SIZE_OPTIONS}
                              placeholder="Select size"
                              ariaLabel="Company size"
                              className="w-full"
                              buttonClassName="h-10"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <Input
                            label="LinkedIn URL"
                            name="linkedin_url"
                            type="url"
                            value={formData.linkedin_url}
                            onChange={handleChange}
                            error={errors.linkedin_url}
                            icon={<LinkedinLogo weight="fill" className="h-4 w-4" />}
                            placeholder="https://linkedin.com/in/janedoe"
                          />
                          <Input
                            label="Twitter URL"
                            name="twitter_url"
                            type="url"
                            value={formData.twitter_url}
                            onChange={handleChange}
                            error={errors.twitter_url}
                            icon={<TwitterLogo className="h-4 w-4" />}
                            placeholder="https://x.com/janedoe"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 px-6 py-3.5 border-t border-border/50 dark:border-white/[0.06] bg-muted/20 dark:bg-white/[0.01] shrink-0">
            <Button variant="outline" size="sm" onClick={requestClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleSubmit}
              loading={loading}
              disabled={!canEditContacts}
              title={canEditContacts ? undefined : 'Your role cannot create or edit contacts'}
            >
              {isEditing ? 'Save changes' : 'Add contact'}
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>

    {/* Unsaved-changes guard. Outside AnimatePresence so opening it does not
        take part in the modal's enter/exit transition. */}
    <ConfirmDialog
      open={showDiscardConfirm}
      title="Discard changes?"
      description={
        isEditing
          ? 'Your edits to this contact have not been saved and will be lost.'
          : 'The contact details you entered have not been saved and will be lost.'
      }
      confirmLabel="Discard"
      cancelLabel="Keep editing"
      tone="destructive"
      onConfirm={() => {
        setShowDiscardConfirm(false);
        onClose();
      }}
      onCancel={() => setShowDiscardConfirm(false)}
    />
    </>
  ), document.body);
}
